import { motion, AnimatePresence } from "framer-motion";
import { Award, CheckCircle2, PlayCircle, Compass, ArrowRight, Link2, Info, Gauge } from "lucide-react";
import { Button } from "@/components/ui/button";
import { StageStepper } from "./StageStepper";
import { fmtCpd } from "./useAcademy";

export function ModeBanner({ linkedin, isAdmin, onGoAdmin }) {
  if (linkedin?.connected) return null;
  return (
    <div data-testid="mode-banner" className="rounded-2xl border border-amber-200 bg-amber-50/70 text-amber-900 px-4 py-3 flex items-start gap-3 mb-6">
      <Link2 className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-600" />
      <div className="text-sm leading-relaxed flex-1">
        <span className="font-medium">Curated Links mode.</span> LinkedIn Learning courses below are hand-picked public links — you'll need your own LinkedIn Learning access to open them. Completion is self-declared.
        {isAdmin && <> Connect the enterprise catalogue API in the <button onClick={onGoAdmin} className="underline font-medium" data-testid="mode-banner-admin-link">Admin tab</button> to replace them with live licensed content.</>}
      </div>
    </div>
  );
}

function Stat({ icon: Icon, value, label, tint, testId }) {
  return (
    <div className={`rounded-2xl p-5 ${tint}`} data-testid={testId}>
      <Icon className="w-5 h-5 mb-2 opacity-90" />
      <div className="font-display text-2xl sm:text-3xl font-bold leading-none">{value}</div>
      <div className="text-xs mt-1.5 opacity-80">{label}</div>
    </div>
  );
}

export function JourneyHeader({ journey, onSelectStage, pending, onAssess }) {
  const { stage, suggested_stage, activity, stages, ready_for_next, next_stage, in_progress, assessed, gaps } = journey;
  const byKey = (k) => stages.find((s) => s.key === k)?.name || k;
  const basis = `${fmtCpd(activity.cpd_credits)} CPD credits · ${activity.proposals_won} proposals won · ${activity.engagements_completed} engagements completed`;
  return (
    <div className="mb-8" data-testid="journey-header">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Stat icon={Compass} value={byKey(stage)} label="Your stage" tint="bg-slate-900 text-white" testId="stat-stage" />
        <Stat icon={Award} value={fmtCpd(activity.cpd_credits)} label={`CPD credits${activity.self_declared_credits ? ` · ${fmtCpd(activity.self_declared_credits)} self-declared` : ""}`} tint="bg-gradient-to-br from-emerald-600 to-teal-600 text-white" testId="cpd-credits" />
        <Stat icon={CheckCircle2} value={activity.courses_completed} label="Courses completed" tint="bg-white border border-slate-200/80 shadow-sm text-slate-900" testId="stat-completed" />
        <Stat icon={PlayCircle} value={in_progress} label="In progress" tint="bg-white border border-slate-200/80 shadow-sm text-slate-900" testId="stat-in-progress" />
      </div>

      <div className="mb-3 flex items-center justify-between flex-wrap gap-2">
        <div className="eyebrow">Your professional journey</div>
        <div className="text-xs text-slate-500 flex items-center gap-1.5" data-testid="suggested-stage-note">
          <Info className="w-3.5 h-3.5" />
          {suggested_stage === stage ? <>Your activity matches <b>{byKey(stage)}</b></> : <>Activity suggests <b>{byKey(suggested_stage)}</b></>} · {basis}
        </div>
      </div>
      <StageStepper stages={stages} current={stage} suggested={suggested_stage} onSelect={onSelectStage} pending={pending} />

      <div data-testid="gap-summary" className={`mt-4 rounded-2xl border px-4 py-3 flex items-center justify-between gap-3 flex-wrap ${assessed ? "bg-white border-slate-200/80" : "bg-slate-50 border-dashed border-slate-300"}`}>
        <div className="text-sm text-slate-700 flex items-start gap-2">
          <Gauge className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
          {!assessed ? (
            <span>Not yet self-assessed — rate yourself in each domain to see your <b>real</b> gaps, not just stage defaults.</span>
          ) : gaps.length === 0 ? (
            <span>Your self-assessment meets the {byKey(stage)} bar in every domain. Keep completing courses to stay on track.</span>
          ) : (
            <span>Biggest gaps: {gaps.map((g, i) => <span key={g.key}><b>{g.name}</b> ({g.gap_label}){i < gaps.length - 1 ? ", " : ""}</span>)} — recommendations below are ordered to close them.</span>
          )}
        </div>
        <Button size="sm" variant={assessed ? "outline" : "default"} onClick={onAssess} data-testid="rate-yourself" className={assessed ? "" : "bg-emerald-600 hover:bg-emerald-700"}>{assessed ? "Update self-assessment" : "Rate yourself"}</Button>
      </div>

      <AnimatePresence>
        {ready_for_next && next_stage && (
          <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} data-testid="next-stage-nudge"
            className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
            <div className="text-sm text-emerald-900"><b>You look ready for {byKey(next_stage)}.</b> Every competency for {byKey(stage)} is on track and no self-assessed gaps remain — update your stage to unlock the next syllabus.</div>
            <Button size="sm" onClick={() => onSelectStage(next_stage)} disabled={pending} data-testid="nudge-advance" className="bg-emerald-600 hover:bg-emerald-700">Move to {byKey(next_stage)} <ArrowRight className="w-4 h-4 ml-1.5" /></Button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
